import { useMemo } from 'react';
import { Autosuggest, FormField } from '@cloudscape-design/components';
import { useConfigs } from '@features/configs';

interface BusinessUnitSelectProps {
  value: string;
  onChange: (value: string) => void;
}

/**
 * Creatable autosuggest for Business Unit tagging.
 * User can type a new value OR select from existing business units.
 * BR-15 Part A: Business Unit field on every config form.
 */
export function BusinessUnitSelect({ value, onChange }: BusinessUnitSelectProps) {
  const { data } = useConfigs();

  // Extract unique business units from all existing configs as suggestions
  const suggestions = useMemo(() => {
    if (!data?.items) return [];
    const units = new Set<string>();
    for (const item of data.items) {
      if (item.businessUnit) {
        units.add(item.businessUnit);
      }
    }
    return Array.from(units).sort().map((u) => ({ value: u }));
  }, [data]);

  return (
    <FormField
      label="Business Unit"
      description="Group this configuration by business area (e.g., Claims, Sales, Service Desk)"
    >
      <Autosuggest
        value={value}
        onChange={({ detail }) => onChange(detail.value)}
        options={suggestions}
        placeholder="Type or select a business unit..."
        empty="No existing business units. Type to create one."
        enteredTextLabel={(val) => `Use: "${val}"`}
      />
    </FormField>
  );
}
